import React, { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { hasModuleAccess, isModuleTabVisible, type ModuleKey } from '../../constants/moduleAccess.ts';
import { useOrgModuleAccess } from '../../hooks/useOrgModuleAccess.ts';
import { downloadModuleAuditExcelSafe } from '../../request/audit/downloadModuleAuditExcel.ts';

export type ModuleAuditDownloadButtonProps = {
  /** Audit resource_type (e.g. "risk", "sox_control"). */
  resourceType: string;
  /** Single module key required to show/use the button. */
  moduleKey?: ModuleKey;
  /** OR of module keys (same as sidebar moduleKeys). Takes precedence when non-empty. */
  moduleKeys?: ModuleKey[];
  /** Optional filename prefix. */
  filenamePrefix?: string;
  /** Optional narrowing within resourceType (see ModuleAuditEventListParams.scope). */
  scope?: string;
  className?: string;
  label?: string;
};

/**
 * Header control: download current-org audit Excel for one resource_type.
 * Hidden when the user lacks module access (org override / role fallback already applied by API).
 */
export function ModuleAuditDownloadButton({
  resourceType,
  moduleKey,
  moduleKeys,
  filenamePrefix,
  scope,
  className,
  label = 'Download Audit',
}: ModuleAuditDownloadButtonProps) {
  const { data: moduleAccess } = useOrgModuleAccess();
  const [exporting, setExporting] = useState(false);

  const canShow =
    moduleKeys && moduleKeys.length > 0
      ? isModuleTabVisible({ moduleKeys }, moduleAccess)
      : moduleKey
        ? hasModuleAccess(moduleAccess, moduleKey)
        : false;

  if (!canShow) {
    return null;
  }

  const handleClick = async () => {
    setExporting(true);
    try {
      await downloadModuleAuditExcelSafe({
        resourceType,
        filters: scope ? { scope } : undefined,
        filenamePrefix: filenamePrefix || `${resourceType}_audit`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={exporting}
      className={
        className ||
        'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors'
      }
      title={`Download ${resourceType} audit log for this organization`}
    >
      {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      {exporting ? 'Exporting…' : label}
    </button>
  );
}

export default ModuleAuditDownloadButton;
